import { useState } from 'react';
import { usePageMeta, Reveal, FORM_ENDPOINT } from '../lib/site';
import { CardHeader } from '../components/Bits';

const EXPECT = [
  ['Thirty minutes, on video', 'One call, no follow-up sequence. Bring whoever runs the counter.'],
  ['Demand side first', 'We start with how golfers find and book you, because that is the reason to switch. Then LinksOS itself: the tee sheet and back office your course would run.'],
  ['Your numbers, live', 'We put your rounds and green fee into the savings calculator on the call and let you audit the arithmetic.'],
  ['No pressure', 'If it is not a fit for your course, we will say so on the call.'],
];

export default function Demo() {
  usePageMeta(
    'Book a demo | MyGolfLinks',
    'Thirty minutes on video. We start with the demand side, walk the parts of LinksOS you care about, and run your numbers live.'
  );
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = Object.fromEntries(new FormData(form).entries());
    if (data.website) {
      setSent(true);
      return;
    }
    setSending(true);
    setErr('');
    try {
      const r = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'demo', ...data }),
      });
      if (!r.ok) throw new Error('send_failed');
      setSent(true);
    } catch {
      setErr("That didn't go through. Try again, or email support@mygolflinks.com and we'll set it up by hand.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main>
      <section style={{ padding: 'clamp(130px, 15vw, 170px) 0 clamp(70px, 9vw, 110px)' }}>
        <div className="wrap">
          <div className="demo-page-grid">
            <Reveal>
              <p className="mono" style={{ fontSize: 12.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fairway)', fontWeight: 500, marginBottom: 20 }}>
                Book a demo
              </p>
              <h1 className="h2" style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3.4rem)' }}>
                See it on your course's numbers.
              </h1>
              <p className="lede" style={{ margin: '18px 0 36px' }}>
                Tell us a little about your operation and we will reply within one business day to
                set a time.
              </p>

              <CardHeader no="01" label="What to expect" />
              <div className="ledger">
                {EXPECT.map(([t, b]) => (
                  <div className="led" key={t} style={{ gridTemplateColumns: '210px 1fr', padding: '18px 0' }}>
                    <span className="led-title" style={{ fontSize: '1rem' }}>{t}</span>
                    <span className="led-body" style={{ fontSize: '0.93rem' }}>{b}</span>
                  </div>
                ))}
              </div>

              <p style={{ marginTop: 28, fontSize: '0.92rem', color: 'var(--mut)', maxWidth: '52ch' }}>
                Suppliers and brands: use the same form and pick your role. We will point the call
                at the MGL Partner Portal instead.
              </p>
            </Reveal>

            <Reveal d={1}>
              <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 16, padding: 'clamp(24px, 3.5vw, 38px)' }}>
                {sent ? (
                  <div style={{ textAlign: 'center', padding: '40px 10px' }}>
                    <div className="mono" style={{ fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fairway)', marginBottom: 14 }}>
                      Request received
                    </div>
                    <h2 className="h3" style={{ fontSize: '1.6rem' }}>You're on the sheet.</h2>
                    <p style={{ color: 'var(--mut)', marginTop: 12, maxWidth: '40ch', marginLeft: 'auto', marginRight: 'auto' }}>
                      We reply within one business day to set a time. Poke around the live sandbox
                      on the courses page while you wait.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} noValidate={false}>
                    <div className="g2-tight">
                      <div className="fg">
                        <label htmlFor="f-name">Your name</label>
                        <input className="fi" id="f-name" name="name" type="text" autoComplete="name" required />
                      </div>
                      <div className="fg">
                        <label htmlFor="f-email">Email</label>
                        <input className="fi" id="f-email" name="email" type="email" autoComplete="email" required />
                      </div>
                    </div>
                    <div className="fg">
                      <label htmlFor="f-course">Course or company</label>
                      <input className="fi" id="f-course" name="course" type="text" autoComplete="organization" required />
                    </div>
                    <div className="g2-tight">
                      <div className="fg">
                        <label htmlFor="f-role">I am a</label>
                        <div className="fwrap-select">
                          <select className="fi" id="f-role" name="role" required defaultValue="">
                            <option value="" disabled>Pick one</option>
                            <option>GM or owner</option>
                            <option>Head pro</option>
                            <option>Course staff</option>
                            <option>Supplier or brand</option>
                            <option>Other</option>
                          </select>
                        </div>
                      </div>
                      <div className="fg">
                        <label htmlFor="f-phone">Phone, optional</label>
                        <input className="fi" id="f-phone" name="phone" type="tel" autoComplete="tel" />
                      </div>
                    </div>
                    <div className="fg">
                      <label htmlFor="f-stack">What runs your tee sheet today?</label>
                      <div className="fwrap-select">
                        <select className="fi" id="f-stack" name="stack" defaultValue="">
                          <option value="">Pick the closest</option>
                          <option>Lightspeed</option>
                          <option>Teesnap</option>
                          <option>foreUP</option>
                          <option>Club Prophet</option>
                          <option>A booking marketplace</option>
                          <option>Pen and paper</option>
                          <option>Something else</option>
                        </select>
                      </div>
                    </div>
                    <div className="fg">
                      <label htmlFor="f-notes">Anything we should make sure to show you?</label>
                      <textarea className="fi" id="f-notes" name="notes" rows="3" />
                    </div>
                    <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                      <label htmlFor="f-website">Leave this field empty</label>
                      <input id="f-website" name="website" type="text" tabIndex="-1" autoComplete="off" />
                    </div>
                    <button type="submit" className="btn btn-gold f-submit" disabled={sending}>
                      {sending ? 'Sending' : 'Request the demo'}
                    </button>
                    {err && (
                      <p style={{ fontSize: '0.88rem', color: 'var(--flag)', marginTop: 12, textAlign: 'center' }}>{err}</p>
                    )}
                    <p style={{ fontSize: '0.8rem', color: 'var(--faint)', textAlign: 'center', marginTop: 12 }}>
                      No mailing list, no drip campaign. We use this to reply, once.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
